// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
/* eslint-disable spire/export-styles */
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { xIsAuthenticated } from "@guestsupply/App/Store/Selectors/GeneralSelectors";
import { xHasRoles } from "@guestsupply/App/Store/Selectors/SessionSelectors";
import { getSettingsCollection } from "@guestsupply/App/Store/Selectors/SettingsSelectors";
import { getDataLayer } from "@guestsupply/App/Utilities/DataLayer";
import { GOOGLE_EVENTS, USER_ROLES } from "@guestsupply/XDL/constants";
import { xScreen } from "@guestsupply/XDL/Utilities";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import * as cssLinter from "css";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    depth = "depth",
    showSignOutLink = "showSignOutLink",
    nodeId = "nodeId",
    leftMargin = "leftMargin",
    marginBottom = "marginBottom",
    customCSS = "customCSS",
    // XNG-CHANGES -GUEST-2689
    showPaymentPortalLink = "showPaymentPortalLink",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.depth]: number;
        [fields.nodeId]: LinkFieldValue;
        [fields.leftMargin]: number;
        [fields.marginBottom]: number;
        [fields.customCSS]: string;
        [fields.showSignOutLink]: boolean;
        // XNG-CHANGES -GUEST-2689
        [fields.showPaymentPortalLink]: boolean;
    };
}

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => ({
    rootPageLink: getPageLinkByNodeId(state, ownProps.fields.nodeId?.value || getCurrentPage(state).nodeId),
    displayChangeCustomerLink: state.context.session?.displayChangeCustomerLink || false,
    paymentPortalLink: getSettingsCollection(state).customSettings?.paymentPortalLink,
    // xng-change: update the allowed roles
    displayAutoDeliveryLink: xHasRoles(state, [
        USER_ROLES.ADMIN,
        USER_ROLES.BUYER2,
        USER_ROLES.BUYER3,
        USER_ROLES.PUNCHOUT,
    ]),
    isPunchOutSession: getIsPunchOutSession(state),
    isAuthenticated: xIsAuthenticated(state),
    displayMGOptionsLink: xHasRoles(state, ["MGAdmin"]),
    // xng-change: add google event
    xGoogleAnalytics: state.xGoogleAnalytics,
    currentPage: getCurrentPage(state).type,
    currentBillTo: getCurrentBillToState(state).value,
});

interface ListStyleProp {
    leftMargin: number;
    marginBottom: number;
}

const ListStyle = styled.ul<ListStyleProp>`
    margin-left: ${props => props.leftMargin}px;
    li {
        margin-bottom: ${props => props.marginBottom}px;
        ${xScreen("lte", "md")} {
            display: inline-block;
            width: 33.33%;
        }
        ${xScreen("lte", "sm")} {
            width: calc(50% - 10px);
            margin-right: 10px;
        }
        ${xScreen("lte", "xs")} {
            width: 100%;
        }
    }
`;

const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface NavigationListStyles {
    linkStyle?: LinkPresentationProps;
}

// Xng-Changes: please check NavigationListExtensions.ts for styles
export const navigationListStyles: NavigationListStyles = {};

const styles = navigationListStyles;

export const NavigationList = (props: Props) => {
    const { isPunchOutSession, cancelPunchOut, signOut, isAuthenticated } = props;
    if (!props.rootPageLink || !props.rootPageLink.children) {
        return null;
    }
    const onSignOutHandler = () => {
        isPunchOutSession ? cancelPunchOut() : signOut();
    };

    // xng-change: add google event
    const handleGoogleEvent = (pageLink: PageLinkModel) => {
        const { xGoogleAnalytics, currentPage, currentBillTo } = props;
        if (xGoogleAnalytics.includeMyAccountQuickLinks) {
            const dataLayer = getDataLayer();

            const googleAnalyticsData = {
                event: GOOGLE_EVENTS.MY_ACCOUNT_QUICK_LINKS,
                page_path: currentPage,
                click_text: pageLink.title,
                destination: pageLink.url,
                brand_code: currentBillTo?.properties?.brand,
                exclusive: currentBillTo?.properties?.exclusive,
                group_descr: currentBillTo?.properties?.groupDescription,
            };

            dataLayer.push(googleAnalyticsData);
        }
    };

    const customCssWrapper = {
        css: css`
            ${props.fields.customCSS}
        `,
    };

    const signOutElement = props.fields.showSignOutLink ? (
        <Link
            className="signout-clickable"
            data-test-selector="user_signout"
            aria-label="sign out"
            onClick={onSignOutHandler}
            color="secondary"
        >
            {translate("Sign Out")}
        </Link>
    ) : null;

    const renderLink = (pageLink: PageLinkModel, currentDepth: number) => {
        if (currentDepth > props.fields.depth) {
            return;
        }

        if (
            pageLink.excludeFromNavigation ||
            (pageLink.type === "ChangeCustomerPage" && !props.displayChangeCustomerLink) ||
            (pageLink && pageLink.type === "AutoDeliveryPage" && !props.displayAutoDeliveryLink) || // XNG-CHANGES: Show AutoDeliveryPage link only for admin and buyer3
            (pageLink && pageLink.type === "ManagementGroupOptionsPage" && !props.displayMGOptionsLink) // XNG-CHANGES GSS-825 : Hide mg options link when no MGAdmin role present
        ) {
            return;
        }

        if (pageLink.type === "VmiUsersPage") {
            const currentMode = getCookie(NavigationModeCookieName);
            if (!currentMode || currentMode === "Storefront") {
                return;
            }
        }

        const nextDepth = currentDepth + 1;

        return (
            <li key={pageLink.id} className="navigation-list-item">
                <Link
                    className="navigation-list-link"
                    data-test-selector={`navigationList-${pageLink.title}`}
                    href={pageLink.url}
                    {...styles.linkStyle}
                    aria-label={pageLink.title}
                    // xng-change: add google event
                    onClick={() => handleGoogleEvent(pageLink)}
                >
                    {pageLink.title}
                </Link>
                {pageLink.children && pageLink.children.length > 0 && (
                    <ListStyle
                        marginBottom={props.fields.marginBottom}
                        leftMargin={props.fields.leftMargin}
                        key={pageLink.id}
                    >
                        {pageLink.children.map(link => renderLink(link, nextDepth))}
                    </ListStyle>
                )}
            </li>
        );
    };

    // XNG-changes - add Payment Portal link
    return (
        <StyledWrapper {...customCssWrapper}>
            <ListStyle
                className="navigation-list"
                marginBottom={props.fields.marginBottom}
                leftMargin={props.fields.leftMargin}
                data-test-selector="navigationList"
            >
                <>
                    {props.rootPageLink.children.map(link => renderLink(link, 1))}
                    {/* XNG-CHANGES -GUEST-2689 add props.fields.showPaymentPortalLink checkbox */}
                    {props.fields.showPaymentPortalLink && props.paymentPortalLink && (
                        <li className="navigation-list-item">
                            {createWidgetElement("PaymentPortal/PaymentPortalLink", {
                                fields: { isNavigationList: true },
                            })}
                        </li>
                    )}
                    {isAuthenticated && signOutElement}
                </>
            </ListStyle>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.navigation-list{
}

.navigation-list-item {
}

.navigation-list-link {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(NavigationList),
    definition: {
        group: "Basic",
        icon: "link-simple",
        fieldDefinitions: [
            {
                name: fields.depth,
                displayName: "Depth",
                editorTemplate: "IntegerField",
                defaultValue: 2,
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.nodeId,
                displayName: "Page",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
                allowUrls: item => false,
                allowCategories: item => false,
                tab: basicTab,
            },
            {
                name: fields.leftMargin,
                displayName: "Left Margin",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                max: 100,
                min: 0,
                tab: basicTab,
            },
            {
                name: fields.marginBottom,
                displayName: "Margin Bottom",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                max: 100,
                min: 0,
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
            {
                name: fields.showSignOutLink,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            // XNG-CHANGES -GUEST-2689
            {
                name: fields.showPaymentPortalLink,
                displayName: "Show Payment Portal",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
